/**
 * Centralized Image URL Service
 * Eliminates redundant path conversion logic across components
 * Consolidates to single VITE_MODAL_API_BASE_URL environment variable
 */

const API_BASE = import.meta.env.VITE_MODAL_API_BASE_URL || '';

export const imageUrlService = {
  /**
   * Convert any image source to displayable URL
   * Priority: base64 > full URL > file path via API
   *
   * @param source - Primary image source (base64, URL, or file path)
   * @param fallback - Fallback source if primary is unavailable
   * @returns Displayable image URL or undefined
   */
  getImageUrl(source?: string, fallback?: string): string | undefined {
    if (!source && !fallback) return undefined;

    // 1. Prefer base64 data URIs (instant display, no network request)
    if (source?.startsWith('data:')) return source;

    // 2. Accept full URLs as-is
    if (source?.startsWith('http://') || source?.startsWith('https://')) {
      return source;
    }

    // 3. Convert file path to API URL
    const path = source || fallback;
    if (!path) return undefined;

    if (!API_BASE) {
      console.warn('[imageUrlService] VITE_MODAL_API_BASE_URL not configured');
      return undefined;
    }

    // Path is already full (e.g., /data/mockups/...), just encode and create API URL
    const encodedPath = encodeURIComponent(path);
    return `${API_BASE}/get_image?path=${encodedPath}`;
  },

  /**
   * Get template image URL (prefer thumbnail for faster loading)
   *
   * @param imagePath - Primary template image path
   * @param thumbnailPath - Optional thumbnail path (preferred if available)
   * @param cacheBuster - Optional timestamp to bust cache (e.g., updated_at)
   * @returns Displayable URL
   */
  getTemplateImageUrl(imagePath?: string, thumbnailPath?: string, cacheBuster?: any): string | undefined {
    // Prefer thumbnail for faster loading
    const url = this.getImageUrl(thumbnailPath || imagePath);

    // Add cache buster if provided
    if (url && cacheBuster) {
      const timestamp = cacheBuster?.seconds
        ? cacheBuster.seconds * 1000
        : (cacheBuster instanceof Date ? cacheBuster.getTime() : cacheBuster);
      return `${url}&t=${timestamp}`;
    }

    return url;
  },

  /**
   * Get mockup image URL (prefer base64 for instant display)
   *
   * @param imageData - Base64 encoded image data (preferred)
   * @param imagePath - File path fallback
   * @returns Displayable URL
   */
  getMockupImageUrl(imageData?: string, imagePath?: string): string | undefined {
    // Prefer base64 for instant display, fall back to path via API
    return this.getImageUrl(imageData, imagePath);
  },


  /**
   * Preload image for better UX (no await needed)
   *
   * @param url - URL to preload
   */
  preloadImage(url?: string): void {
    if (!url) return;

    const img = new Image();
    img.src = url;
    // No need to do anything with the image, browser caches it
  },

  /**
   * Generate fallback mask image for templates without mask
   * Creates a white image with a red box in center (default print area)
   *
   * @returns Promise<string> - Data URL of generated mask image
   */
  async generateFallbackMaskImage(): Promise<string> {
    return new Promise((resolve) => {
      // Create canvas for fallback mask (512x512 to match typical template size)
      const canvas = document.createElement('canvas');
      canvas.width = 512;
      canvas.height = 512;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.error('Could not get canvas context for fallback mask');
        resolve('');
        return;
      }

      // White background
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, 512, 512);

      // Red box in center (80% of canvas size, centered)
      // This represents the default printable area
      const boxSize = 512 * 0.8;
      const boxX = (512 - boxSize) / 2;
      const boxY = (512 - boxSize) / 2;

      ctx.fillStyle = '#FF0000';
      ctx.fillRect(boxX, boxY, boxSize, boxSize);

      // Convert to data URL
      const dataUrl = canvas.toDataURL('image/png');
      console.log('✅ Generated fallback mask image');
      resolve(dataUrl);
    });
  },

  /**
   * Get API base URL for debugging
   */
  getApiBase(): string {
    return API_BASE;
  }
};
